import { useEffect, useState } from "react";
import { HitLoadingBlock } from "@/components/loading";
import { api } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Scatter, ComposedChart, Area,
} from "recharts";

const RANGES = [
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "all", label: "Todo" },
];

function fmtDateShort(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("es-EC", { day: "2-digit", month: "short" });
}

export default function PriceHistoryChart({ cardId, language, variant }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/catalog/${cardId}/${language}/${variant || "normal"}/history`, { params: { range } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [cardId, language, variant, range]);

  const marketPoints = (data?.market || []).map((m) => ({
    date: m.date,
    avg: m.avg,
    median: m.median,
    listings: m.listings,
  }));
  const salesPoints = (data?.sales || []).map((s) => ({
    date: s.date,
    sale: s.price,
    quantity: s.quantity,
  }));

  // Merge para eje X unificado: usamos el mismo array combinando por fecha
  const dateMap = new Map();
  for (const m of marketPoints) {
    dateMap.set(m.date, { date: m.date, avg: m.avg, median: m.median });
  }
  for (const s of salesPoints) {
    const existing = dateMap.get(s.date) || { date: s.date };
    // Si hay múltiples ventas mismo día, sumamos como puntos separados via array
    existing.sale = existing.sale != null ? Math.round((existing.sale + s.sale) / 2 * 100) / 100 : s.sale;
    dateMap.set(s.date, existing);
  }
  const chartData = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));

  const hasData = chartData.length > 0;
  const totalSales = salesPoints.length;

  return (
    <div className="bg-[#101010] border border-white/10 rounded-[22px] p-4 lg:p-5" data-testid="price-history-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="sx-mono text-[10px] uppercase tracking-[.14em] text-white/50" data-testid="price-history-title">Historial de precios</div>
          <div className="text-sm text-neutral-400 mt-0.5">
            Línea = promedio histórico (todas las publicaciones) · Puntos = ventas cerradas reales
          </div>
        </div>
        <div className="flex gap-1 bg-[#080808] rounded-full p-1 border border-white/10">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              data-testid={`price-range-${r.key}`}
              className={`px-3.5 h-8 text-xs font-semibold rounded-full transition-colors ${
                range === r.key
                  ? "bg-[#FF4438] text-[#080808]"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-56" data-testid="price-history-loading">
          <HitLoadingBlock section="historial" />
        </div>
      ) : !hasData ? (
        <div className="h-56 flex flex-col items-center justify-center text-neutral-500 text-sm text-center border border-dashed border-[#262626] rounded">
          <div>Aún no hay historial suficiente para graficar.</div>
          <div className="text-xs mt-2 text-neutral-600">
            Los precios de mercado se registran cada día que hay listados activos y las ventas reales aparecen al liberarse.
          </div>
        </div>
      ) : (
        <>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="hitArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF4438" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF4438" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" vertical={false} />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#999" }}
                  tickFormatter={fmtDateShort}
                  minTickGap={20}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#999" }}
                  tickFormatter={(v) => `$${v}`}
                  domain={["auto", "auto"]}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: "#0A0A0A", border: "1px solid #262626", fontSize: 12 }}
                  labelStyle={{ color: "#fff" }}
                  formatter={(v, name) => {
                    if (v == null) return ["—", name];
                    const label = name === "avg" ? "Promedio histórico"
                                : name === "median" ? "Mediana histórica"
                                : name === "sale" ? "Venta real"
                                : name;
                    return [`$${v}`, label];
                  }}
                  labelFormatter={fmtDateShort}
                />
                <Area type="monotone" dataKey="avg" stroke="none" fill="url(#hitArea)" connectNulls isAnimationActive={false} legendType="none" tooltipType="none" />
                <Line
                  type="monotone"
                  dataKey="avg"
                  stroke="#FF4438"
                  strokeWidth={2.5}
                  dot={(p) => (p.index === chartData.length - 1 && p.value != null
                    ? <g key="last"><circle cx={p.cx} cy={p.cy} r={9} fill="rgba(255,68,56,.25)" /><circle cx={p.cx} cy={p.cy} r={4} fill="#FF4438" /></g>
                    : <g key={p.index} />)}
                  connectNulls
                  isAnimationActive={false}
                  name="avg"
                />
                <Scatter
                  dataKey="sale"
                  fill="#10B981"
                  stroke="#10B981"
                  strokeWidth={2}
                  shape="circle"
                  name="sale"
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-neutral-400">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 bg-[#FF4438]" />
              Precio de mercado
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Ventas reales ({totalSales})
            </div>
          </div>
        </>
      )}
    </div>
  );
}
